import { useEffect, useMemo, useState } from "react";
import { FileText, Filter, RotateCcw, User, LayoutGrid, Tag, Calendar } from "lucide-react";
import LogService from "../../services/resource/logService";
import PageAPI from "../../componentes/pagination/Pagination-Page";
import { formatLocalDateTime } from "../../utils/formatDate";

const initialLogs = {
    content: [],
    page: {
        number: 0,
        size: 20,
        totalElements: 0,
        totalPages: 0
    }
};

const filtrosVazios = {
    usuario: "",
    dashboard: "",
    tipo: "",
    dataInicio: "",
    dataFim: ""
};

const TIPO_STYLES = [
    { match: ["cria", "insert", "novo", "adicion"], className: "bg-success/15 text-success" },
    { match: ["edi", "atualiz", "update", "altera"], className: "bg-primary/15 text-primary" },
    { match: ["exclu", "delet", "remov"], className: "bg-destructive/15 text-destructive" },
    { match: ["acess", "login", "visualiz"], className: "bg-muted text-muted-foreground" },
];

function tipoBadgeClass(tipo) {
    const lower = (tipo || "").toLowerCase();
    const found = TIPO_STYLES.find(t => t.match.some(m => lower.includes(m)));
    return found ? found.className : "bg-accent text-accent-foreground";
}

function formatUsuario(usuario) {
    if (!usuario) return "-";
    return usuario.split("@")[0];
}

export default function LogsTab() {
    const logService = useMemo(() => new LogService(), []);

    const [logs, setLogs] = useState(initialLogs);
    const [loading, setLoading] = useState(false);

    const [opcoes, setOpcoes] = useState({ usuarios: [], dashboards: [], tipos: [] });

    const [page, setPage] = useState({
        page: 0,
        size: 20,
        sortKey: "data",
        sortDirection: "desc"
    });

    const [sortConfig, setSortConfig] = useState({
        key: "data",
        direction: "desc"
    });

    const [filtros, setFiltros] = useState(filtrosVazios);

    useEffect(() => {
        logService.obterFiltros()
            .then(res => setOpcoes({
                usuarios: res.data.usuarios || [],
                dashboards: res.data.dashboards || [],
                tipos: res.data.tipos || []
            }))
            .catch(() => { });
    }, [logService]);

    useEffect(() => {
        setLoading(true);
        logService
            .listaPaginada(filtros, page)
            .then(res => setLogs(res.data))
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page]);

    const aplicarFiltros = (novosFiltros) => {
        setFiltros(novosFiltros);
        setPage(prev => ({ ...prev, page: 0 }));
        setLoading(true);
        logService
            .listaPaginada(novosFiltros, { ...page, page: 0 })
            .then(res => setLogs(res.data))
            .finally(() => setLoading(false));
    };

    const onChangeFiltro = (e) => {
        aplicarFiltros({ ...filtros, [e.target.name]: e.target.value });
    };

    const limparFiltros = () => {
        aplicarFiltros(filtrosVazios);
    };

    const filtrosAtivos = Object.values(filtros).some(v => v);

    const handleSort = (key) => {
        const nextDirection =
            sortConfig.key === key && sortConfig.direction === "asc"
                ? "desc"
                : "asc";

        setSortConfig({ key, direction: nextDirection });

        setPage(prev => ({
            ...prev,
            page: 0,
            sortKey: key,
            sortDirection: nextDirection
        }));
    };

    const colunas = [
        { label: 'Data', value: 'data' },
        { label: 'Usuário', value: 'usuario' },
        { label: 'Programa', value: 'dashboard' },
        { label: 'Tipo', value: 'tipo' },
        { label: 'Campos', value: 'camposAlterados' },
        { label: 'Descrição', value: 'descricao' },
        { label: 'IP', value: 'ip' },
    ];

    return (
        <div className="surface-card p-6">

            {/* Título */}
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                    <div className="bg-accent p-2 rounded-lg">
                        <FileText className="text-primary w-5 h-5" />
                    </div>
                    <div>
                        <h2 className="font-display text-xl font-semibold text-foreground">Logs do Sistema</h2>
                        <p className="text-sm text-muted-foreground">Histórico de ações realizadas nos dashboards</p>
                    </div>
                </div>
                <span className="text-sm text-muted-foreground bg-muted px-3 py-1.5 rounded-lg">
                    {logs.page.totalElements} registro{logs.page.totalElements === 1 ? "" : "s"}
                </span>
            </div>

            {/* Filtros */}
            <div className="border border-border rounded-xl p-4 mb-6 bg-muted/30">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-2 text-sm font-medium text-foreground">
                        <Filter className="w-4 h-4 text-primary" />
                        <span>Filtros</span>
                    </div>
                    {filtrosAtivos && (
                        <button
                            onClick={limparFiltros}
                            className="flex items-center space-x-1.5 text-xs text-muted-foreground hover:text-destructive transition-colors duration-150"
                        >
                            <RotateCcw className="w-3.5 h-3.5" />
                            <span>Limpar filtros</span>
                        </button>
                    )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
                    <div>
                        <label className="flex items-center gap-1 text-xs font-medium text-muted-foreground mb-1">
                            <User className="w-3.5 h-3.5" /> Usuário
                        </label>
                        <select
                            name="usuario"
                            value={filtros.usuario}
                            onChange={onChangeFiltro}
                            className="w-full border border-input rounded-lg px-3 py-2 text-sm bg-card text-foreground transition-colors duration-150 focus:border-primary focus:ring-2 focus:ring-primary/15 outline-none"
                        >
                            <option value="">Todos</option>
                            {opcoes.usuarios.map(u => (
                                <option key={u} value={u}>{formatUsuario(u)}</option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="flex items-center gap-1 text-xs font-medium text-muted-foreground mb-1">
                            <LayoutGrid className="w-3.5 h-3.5" /> Dashboard
                        </label>
                        <select
                            name="dashboard"
                            value={filtros.dashboard}
                            onChange={onChangeFiltro}
                            className="w-full border border-input rounded-lg px-3 py-2 text-sm bg-card text-foreground transition-colors duration-150 focus:border-primary focus:ring-2 focus:ring-primary/15 outline-none"
                        >
                            <option value="">Todos</option>
                            {opcoes.dashboards.map(d => (
                                <option key={d} value={d}>{d}</option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="flex items-center gap-1 text-xs font-medium text-muted-foreground mb-1">
                            <Tag className="w-3.5 h-3.5" /> Tipo
                        </label>
                        <select
                            name="tipo"
                            value={filtros.tipo}
                            onChange={onChangeFiltro}
                            className="w-full border border-input rounded-lg px-3 py-2 text-sm bg-card text-foreground transition-colors duration-150 focus:border-primary focus:ring-2 focus:ring-primary/15 outline-none"
                        >
                            <option value="">Todos</option>
                            {opcoes.tipos.map(t => (
                                <option key={t} value={t}>{t}</option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="flex items-center gap-1 text-xs font-medium text-muted-foreground mb-1">
                            <Calendar className="w-3.5 h-3.5" /> Data inicial
                        </label>
                        <input
                            type="datetime-local"
                            name="dataInicio"
                            value={filtros.dataInicio}
                            onChange={onChangeFiltro}
                            className="w-full border border-input rounded-lg px-3 py-2 text-sm bg-card text-foreground transition-colors duration-150 focus:border-primary focus:ring-2 focus:ring-primary/15 outline-none"
                        />
                    </div>

                    <div>
                        <label className="flex items-center gap-1 text-xs font-medium text-muted-foreground mb-1">
                            <Calendar className="w-3.5 h-3.5" /> Data final
                        </label>
                        <input
                            type="datetime-local"
                            name="dataFim"
                            value={filtros.dataFim}
                            onChange={onChangeFiltro}
                            className="w-full border border-input rounded-lg px-3 py-2 text-sm bg-card text-foreground transition-colors duration-150 focus:border-primary focus:ring-2 focus:ring-primary/15 outline-none"
                        />
                    </div>
                </div>
            </div>

            {/* Tabela */}
            <div className="overflow-x-auto rounded-lg border border-border">
                <table className="w-full text-sm">
                    <thead className="bg-table-header text-table-header-fg">
                        <tr>
                            {colunas.map(col => (
                                <th
                                    key={col.value}
                                    onClick={() => handleSort(col.value)}
                                    className="px-4 py-2.5 text-left cursor-pointer select-none hover:brightness-95 transition-all duration-150"
                                >
                                    {col.label}
                                    {sortConfig.key === col.value && (sortConfig.direction === "asc" ? " ↑" : " ↓")}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            Array.from({ length: 6 }).map((_, i) => (
                                <tr key={i} className="border-t border-table-border">
                                    {colunas.map(col => (
                                        <td key={col.value} className="px-4 py-3">
                                            <div className="h-3.5 bg-muted rounded animate-pulse w-4/5" />
                                        </td>
                                    ))}
                                </tr>
                            ))
                        ) : logs.content.length === 0 ? (
                            <tr>
                                <td colSpan={colunas.length} className="text-center py-10">
                                    <div className="flex flex-col items-center gap-2 text-muted-foreground">
                                        <FileText className="w-8 h-8 opacity-40" />
                                        <span>Nenhum log encontrado para os filtros selecionados.</span>
                                    </div>
                                </td>
                            </tr>
                        ) : (
                            logs.content.map(log => (
                                <tr key={log.id} className="border-t border-table-border hover:bg-table-row-hover transition-colors duration-150">
                                    <td className="px-4 py-2.5 text-foreground whitespace-nowrap">{formatLocalDateTime(log.data)}</td>
                                    <td className="px-4 py-2.5 text-foreground">{formatUsuario(log.usuario)}</td>
                                    <td className="px-4 py-2.5 text-foreground">{log.dashboard}</td>
                                    <td className="px-4 py-2.5">
                                        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${tipoBadgeClass(log.tipo)}`}>
                                            {log.tipo}
                                        </span>
                                    </td>
                                    <td className="px-4 py-2.5 text-xs text-muted-foreground max-w-xs truncate" title={log.camposAlterados}>{log.camposAlterados}</td>
                                    <td className="px-4 py-2.5 text-xs text-muted-foreground max-w-sm truncate" title={log.descricao}>{log.descricao}</td>
                                    <td className="px-4 py-2.5 text-xs text-muted-foreground whitespace-nowrap">{log.ip || "-"}</td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Paginação */}
            <PageAPI
                page={logs.page.number}
                size={logs.page.size}
                totalElements={logs.page.totalElements}
                totalPages={logs.page.totalPages}
                loading={loading}
                onPageChange={(newPage) =>
                    setPage(prev => ({ ...prev, page: newPage }))
                }
                onPageSizeChange={(newSize) =>
                    setPage(prev => ({ ...prev, size: newSize, page: 0 }))
                }
            />
        </div>
    );
}
